import React, { useMemo } from 'react';
import * as FieldComponents from './fields';

/**
 * Componente para renderizar un campo dinámico de formulario
 * @param {Object} field - Configuración del campo
 * @param {number} index - Índice del campo
 * @param {any} value - Valor actual del campo
 * @param {Function} onChange - Callback cuando cambia el valor
 * @param {string} formId - ID del formulario (para keys únicas)
 */
function FormField({ field, index, value, onChange, formId }) {
    // Generar automáticamente el mapping de tipos de campo a componentes
    // basándose en los nombres de los componentes importados
    // Ej: TextField -> text, SelectField -> select, TextAreaField -> textarea
    const fieldComponents = useMemo(() => {
        const mapping = {};

        Object.entries(FieldComponents).forEach(([componentName, Component]) => {
            // Extraer el tipo del nombre del componente (ej: TextField -> text)
            const fieldType = componentName
                .replace('Field', '')  // Remover 'Field' del nombre
                .replace(/([A-Z])/g, ' $1')  // Agregar espacio antes de mayúsculas
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '');  // Remover espacios

            mapping[fieldType] = Component;

            // Casos especiales para tipos compuestos
            if (componentName === 'TextAreaField') {
                mapping['textarea'] = Component;
            }
        });

        // Agregar alias para que TextField maneje también 'email'
        if (mapping.text) {
            mapping.email = mapping.text;
        }

        return mapping;
    }, []);

    // Obtener el componente correspondiente al tipo de campo
    const FieldComponent = fieldComponents[field.type];

    // Si no existe el componente para el tipo de campo, mostrar mensaje de error
    if (!FieldComponent) {
        return <p className="text-sm text-content-muted">Tipo de campo no soportado: {field.type}</p>;
    }

    const fieldId = `${formId}_${field.name}_${index}`;

    return (
        <div key={fieldId}>
            {field.type !== 'checkbox' && (
                <label className="block text-sm font-medium text-content mb-2">
                    {field.label} {field.required && <span className="text-error">*</span>}
                </label>
            )}
            <FieldComponent
                field={field}
                index={index}
                value={value}
                onChange={onChange}
                formId={formId}
            />
        </div>
    );
}

export default FormField;
